/**
 * Message Cache
 *
 * Manages caching of messages per conversation
 */

import { EnhancedMessage } from "@/types/centralizedChat";
import { logger } from "@/utils/logger";
import {
  MessageCacheEntry,
  CACHE_TTL_MS,
  STALE_WHILE_REVALIDATE_MS,
} from "./types";

class MessageCache {
  private cache = new Map<string, MessageCacheEntry>();
  private inFlightRequests = new Map<string, Promise<MessageCacheEntry>>();

  get(conversationId: string, page: number = 1): MessageCacheEntry | null {
    const key = `${conversationId}:${page}`;
    const entry = this.cache.get(key);

    if (!entry) return null;

    const age = Date.now() - entry.timestamp;

    // Return fresh cache
    if (age < CACHE_TTL_MS) {
      return entry;
    }

    // Return stale cache (will be revalidated)
    if (age < STALE_WHILE_REVALIDATE_MS) {
      return entry;
    }

    // Cache expired
    return null;
  }

  set(
    conversationId: string,
    page: number,
    messages: EnhancedMessage[],
    pagination: MessageCacheEntry["pagination"]
  ): void {
    const key = `${conversationId}:${page}`;
    this.cache.set(key, {
      data: messages,
      pagination,
      timestamp: Date.now(),
    });

    logger.debug("Message cache updated", {
      component: "MessageCache",
      conversationId,
      page,
      messageCount: messages.length,
    });
  }

  invalidate(conversationId?: string): void {
    if (conversationId) {
      // Remove all pages for this conversation
      const keysToDelete: string[] = [];
      for (const key of this.cache.keys()) {
        if (key.startsWith(`${conversationId}:`)) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.cache.delete(key));

      logger.debug("Invalidated message cache", {
        component: "MessageCache",
        conversationId,
        pagesRemoved: keysToDelete.length,
      });
    } else {
      this.cache.clear();
      logger.debug("Cleared all message cache", {
        component: "MessageCache",
      });
    }
  }

  trackInFlight(key: string, promise: Promise<MessageCacheEntry>): void {
    this.inFlightRequests.set(key, promise);
    promise.finally(() => {
      this.inFlightRequests.delete(key);
    });
  }

  getInFlight(key: string): Promise<MessageCacheEntry> | undefined {
    return this.inFlightRequests.get(key);
  }

  clear(): void {
    this.cache.clear();
    this.inFlightRequests.clear();
  }
}

export const messageCache = new MessageCache();
